'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Menu, X } from 'lucide-react';
import SearchBar from '../common/SearchBar';
import LanguageSelector from '../common/LanguageSelector';
import type { Game } from '@/types';
import type { LanguageCode } from '@/lib/games';

interface HeaderProps {
  currentGame?: Game;
  lang: LanguageCode;
}

const Header = ({ currentGame, lang }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = {
    home: {
      en: 'Cat Chaos Prankster',
      ja: 'キャット カオス プランクスター',
      es: 'Gato Caos Bromista',
      ko: '캣 카오스 프랭크스터',
      th: 'แมววุ่นวายตัวป่วน',
      ru: 'Кот-Проказник Хаоса',
      vi: 'Mèo Tinh Quái Nghịch Ngợm',
      kk: 'Бұзақы Мысық',
      id: 'Kucing Kacau Jahil',
      de: 'Katzen-Chaos-Schelm'
    },
    ballPool: {
      en: 'Cat Evolution',
      ja: 'キャットエボリューション',
      es: 'Evolución del Gato',
      ko: '고양이 진화',
      th: 'วิวัฒนาการแมว',
      ru: 'Эволюция Кота',
      vi: 'Tiến Hóa Mèo',
      kk: 'Мысық Эволюциясы',
      id: 'Evolusi Kucing',
      de: 'Katzen-Evolution'
    },
    puzzleMaster: {
      en: 'Rolling Cat',
      ja: 'ローリング キャット',
      es: 'Gato Rodante',
      ko: '구르는 고양이',
      th: 'แมวกลิ้ง',
      ru: 'Кот на Колёсах',
      vi: 'Mèo Lăn',
      kk: 'Домалақ Мысық',
      id: 'Kucing Menggelinding',
      de: 'Rollende Katze'
    },
    stacking: {
      en: 'Banana Cat Escape',
      ja: 'バナナキャットエスケープ',
      es: 'Escape del Gato Banana',
      ko: '바나나 고양이 탈출',
      th: 'แมวกล้วยหนีรอด',
      ru: 'Побег Кота-Банана',
      vi: 'Mèo Chuối Trốn Thoát',
      kk: 'Банан Мысық Қашу',
      id: 'Pelarian Kucing Pisang',
      de: 'Bananen-Katzen-Flucht'
    },
    wooden: {
      en: 'Cute Cat Jigsaw Puzzle',
      ja: 'かわいい猫のジグソーパズル',
      es: 'Rompecabezas de Gato Lindo',
      ko: '귀여운 고양이 직소 퍼즐',
      th: 'ตัวต่อจิ๊กซอว์แมวน่ารัก',
      ru: 'Пазл с Милым Котом',
      vi: 'Ghép Hình Mèo Dễ Thương',
      kk: 'Сүйкімді Мысық Құрастырмасы',
      id: 'Puzzle Jigsaw Kucing Lucu',
      de: 'Süßes Katzen-Puzzle'
    }
  };

  const getGameLink = (gameId: string) => {
    return lang === 'en' ? `/${gameId}` : `/${lang}/${gameId}`;
  };

  const getHomeLink = () => {
    return lang === 'en' ? '/' : `/${lang}`;
  };

  const navigation = [
    { name: navLinks.home[lang], href: getHomeLink() },
    { name: navLinks.ballPool[lang], href: getGameLink('Cat-Evolution') },
    { name: navLinks.puzzleMaster[lang], href: getGameLink('Rolling-Cat') },
    { name: navLinks.stacking[lang], href: getGameLink('Banana-Cat-Escape') },
    { name: navLinks.wooden[lang], href: getGameLink('cute-cat-jigsaw-puzzle') },
  ];

  return (
    <header className="header-container">
      <nav className="nav-container">
        <div className="nav-left">
          <Link href={getHomeLink()} className="logo-link">
            <Image
              src="/images/games/cat-chaos-prankster.jpeg"
              alt="Cat Chaos Prankster"
              width={48}
              height={48}
              className="logo-image"
            />
            <span className="logo-text">{navLinks.home[lang]}</span>
          </Link>

          <div className="desktop-nav">
            {navigation.slice(1).map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="nav-link"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="nav-right">
          <LanguageSelector />
          <SearchBar />
          <button
            className="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="menu-icon" />
            ) : (
              <Menu className="menu-icon" />
            )}
          </button>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-links">
            {navigation.slice(1).map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="mobile-nav-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {currentGame && (
        <div className="game-header">
          <h1 className="game-title gradient-text">
            {currentGame.title}
          </h1>
          <p className="game-slogan">
            {currentGame.slogan}
          </p>
        </div>
      )}
    </header>
  );
};

export default Header;